import {
    Badge,
    Box,
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    HStack,
    Skeleton,
    Text,
    useDisclosure,
    VStack,
} from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { AbsencePeriod } from '../../types';
import { useGetAbsenceById, useCancelAbsence } from '../../query/resource-hooks/absence';
import { AbsenceReviewModal } from './AbsenceReviewModal';
import { CreateAbsenceDrawer } from './CreateAbsenceDrawer';
import { useSuToast } from '../../hooks/useSuToast';
import { useAlertDialog } from '../../hooks/useAlertDialog';
import { AbsenceStatusBadge } from './AbsenceStatusBadge';
import { AbsenceTypeBadge } from './AbsenceTypeBadge';
import { useAuthStore } from '../../store/auth/authStore';
import UserPolicy from '../../policies/UserPolicy';
import { useSuIntl } from '../../hooks/useSuIntl';
import { BadgeValueField } from '../common/BadgeValueField';
import { TimeRangeVisualizer } from '../common/DateRangeVisualizer';
import { TextValueField } from '../common/TextValueField';
import { AbsenceReviewStatusBadge } from './AbsenceReviewStatusBadge';

type AbsenceDetailDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    absenceId?: number | null;
    onActionComplete: () => void;
};

export const AbsenceDetailDrawer: React.FC<AbsenceDetailDrawerProps> = ({ isOpen, onClose, absenceId, onActionComplete }) => {
    const intl = useIntl();
    const { t, formatDate } = useSuIntl();
    const toast = useSuToast();
    const loggedUser = useAuthStore((s) => s.user);
    const reviewModal = useDisclosure();

    const absenceQuery = useGetAbsenceById(absenceId);
    const absence = absenceQuery.data;

    const cancelAbsence = useCancelAbsence(
        () => {
            toast.success(intl.formatMessage({ id: 'absence.cancelled' }));
            onActionComplete();
            onClose();
        },
        () => {
            toast.error(intl.formatMessage({ id: 'absence.error.cancel' }));
        }
    );

    const cancelDialog = useAlertDialog(
        intl.formatMessage({ id: 'absence.cancel' }),
        intl.formatMessage({ id: 'editor.close' }),
        intl.formatMessage({ id: 'absence.cancel' }),
        intl.formatMessage({ id: 'absence.cancel.confirm' }),
        cancelAbsence.isLoading
    );

    function handleReviewSuccess() {
        onActionComplete();
        onClose();
    }

    const canReview = loggedUser && UserPolicy.canReviewAbsence(loggedUser, absence);
    const canCancel = loggedUser && UserPolicy.canCancelAbsence(loggedUser, absence);
    const canUpdate = loggedUser && UserPolicy.canUpdateAbsence(loggedUser, absence);

    const editDrawer = useDisclosure();

    return (
        <Drawer size={'md'} isOpen={isOpen} onClose={onClose}>
            <DrawerOverlay />
            <DrawerContent m={{ sm: 0, md: 2 }} borderRadius={'xl'}>
                <DrawerHeader borderBottomWidth="1px">{intl.formatMessage({ id: 'absence.detail' })}</DrawerHeader>
                <DrawerCloseButton />
                <DrawerBody>
                    {absenceQuery.isLoading ? (
                        <VStack spacing={4}>
                            <Skeleton height="20px" width="100%" />
                            <Skeleton height="20px" width="100%" />
                            <Skeleton height="20px" width="100%" />
                            <Skeleton height="20px" width="100%" />
                        </VStack>
                    ) : absence ? (
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <Text fontSize={'large'} fontWeight={600}>
                                    {absence.user?.surname} {absence.user?.firstName}
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                    {t(absence.user?.role as string)}
                                </Text>
                            </Box>
                            <HStack justifyContent={'center'}>
                                <TimeRangeVisualizer dateFrom={formatDate(absence.dateFrom)} dateTo={formatDate(absence.dateTo)} />
                                {absence.period === AbsencePeriod.HALF_DAY && (
                                    <Badge colorScheme="orange">{intl.formatMessage({ id: 'absence.halfDay' })}</Badge>
                                )}
                            </HStack>

                            <HStack>
                                <Box flex={1}>
                                    <BadgeValueField label={t('type')} value={t(`absence.type.${absence.type}`)}>
                                        <AbsenceTypeBadge type={absence.type} />
                                    </BadgeValueField>
                                </Box>
                                <Box flex={1}>
                                    <BadgeValueField label={t('field.status')} value={t(`absence.status.${absence.status}`)}>
                                        <HStack>
                                            <AbsenceStatusBadge status={absence.status} />
                                            <AbsenceReviewStatusBadge
                                                date={absence.reviewedAt || absence.createdAt}
                                                status={absence.status}
                                            />
                                        </HStack>
                                    </BadgeValueField>
                                </Box>
                            </HStack>

                            <HStack>
                                {absence.requester && (
                                    <Box flex={1}>
                                        <TextValueField label={t('absence.requestedBy')} value={absence.requester?.fullName} />
                                    </Box>
                                )}

                                {absence.reviewer && (
                                    <Box flex={1}>
                                        <TextValueField
                                            label={t('absence.reviewedBy')}
                                            value={`${absence.reviewer?.fullName} (${absence?.reviewedAt ? formatDate(absence.reviewedAt) : '-'})`}
                                        />
                                    </Box>
                                )}
                            </HStack>

                            {absence.note && (
                                <Box p={2}>
                                    <Text overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis" fontSize={'lg'} fontWeight={'600'}>
                                        {t('note')}
                                    </Text>
                                    <Text pl={2}>{absence.note} </Text>
                                </Box>
                            )}
                        </VStack>
                    ) : null}
                </DrawerBody>
                <DrawerFooter borderTopWidth="1px">
                    <HStack spacing={2}>
                        {canUpdate && (
                            <Button variant="outline" onClick={editDrawer.onOpen}>
                                {intl.formatMessage({ id: 'absence.update' })}
                            </Button>
                        )}
                        {canReview && (
                            <Button colorScheme="primary" onClick={reviewModal.onOpen}>
                                {intl.formatMessage({ id: 'absence.review' })}
                            </Button>
                        )}
                        {canCancel && (
                            <Button
                                colorScheme="red"
                                variant="outline"
                                isLoading={cancelAbsence.isLoading}
                                onClick={() =>
                                    cancelDialog.showDialog(() => {
                                        if (absenceId) cancelAbsence.mutate(absenceId);
                                    })
                                }
                            >
                                {intl.formatMessage({ id: 'absence.cancel' })}
                            </Button>
                        )}
                    </HStack>
                </DrawerFooter>
            </DrawerContent>
            <cancelDialog.Dialog />
            {absenceId && (
                <AbsenceReviewModal
                    isOpen={reviewModal.isOpen}
                    onClose={reviewModal.onClose}
                    absenceId={absenceId}
                    onSuccess={handleReviewSuccess}
                />
            )}
            {absence && (
                <CreateAbsenceDrawer
                    isOpen={editDrawer.isOpen}
                    onClose={editDrawer.onClose}
                    onSuccess={() => {
                        absenceQuery.refetch();
                        onActionComplete();
                    }}
                    absenceRecord={absence}
                />
            )}
        </Drawer>
    );
};
